import React, { useEffect, useRef, useState } from 'react'
import { useDimensions } from '@/hooks'
import { convertPointerToCanvasCoordinates, FieldCoordinateSystem } from '@/utils'
import type { Coordinate, Drawing, PathStyle } from '@/types'
import { processSharpPath } from '@/utils/SharpPath.utils'
import { processSmoothPath } from '@/utils/SmoothPath.utils'
import { findPlayerSnapTarget, type PlayerForSnap } from '@/utils/Drawing.utils'
import { PLAYER_RADIUS_FEET } from '@/constants'

interface FreehandCaptureProps {
	coordSystem: FieldCoordinateSystem
	style: PathStyle
	isActive: boolean
	autoCorrect: boolean
	onCommit: (_drawing: Drawing) => void
	players?: PlayerForSnap[]
	zoom?: number
	panX?: number
	panY?: number
}

/**
 * Captures freehand canvas strokes and converts them to drawings.
 */
export function FreehandCapture({
									coordSystem,
									style,
									isActive,
									autoCorrect: _autoCorrect,
									onCommit,
									players,
									zoom = 1,
									panX = 0,
									panY = 0,
								}: FreehandCaptureProps) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const [isDrawing, setIsDrawing] = useState(false)
	const [points, setPoints] = useState<Coordinate[]>([])
	// Track canvas dimensions to trigger resize
	const canvasDimensions = useDimensions(canvasRef)

	useEffect(() => {
		if (!isActive) {
			resetCanvas()
		}
	}, [isActive])

	function resetCanvas() {
		const canvas = canvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return
		ctx.clearRect(0, 0, canvas.width, canvas.height)
	}

	function startDrawing(event: React.PointerEvent<HTMLCanvasElement>) {
		if (!isActive) return
		const canvas = canvasRef.current
		if (!canvas) return

		const coords = convertPointerToCanvasCoordinates(event, canvas, zoom, coordSystem)
		if (!coords) return

		const ctx = canvas.getContext('2d')
		if (!ctx) return

		ctx.strokeStyle = style.color
		ctx.lineWidth = style.strokeWidth * coordSystem.scale
		ctx.lineCap = 'round'
		ctx.lineJoin = 'round'
		ctx.beginPath()
		ctx.moveTo(coords.canvasPixel.x, coords.canvasPixel.y)

		setPoints([coords.feet])
		setIsDrawing(true)
	}

	function draw(event: React.PointerEvent<HTMLCanvasElement>) {
		if (!isDrawing) return
		const canvas = canvasRef.current
		if (!canvas) return

		const coords = convertPointerToCanvasCoordinates(event, canvas, zoom, coordSystem)
		if (!coords) return

		const ctx = canvas.getContext('2d')
		if (!ctx) return

		ctx.lineTo(coords.canvasPixel.x, coords.canvasPixel.y)
		ctx.stroke()

		setPoints((prev) => [...prev, coords.feet])
	}

	function stopDrawing() {
		if (!isDrawing) return
		setIsDrawing(false)
		resetCanvas()
		commitDrawing(points)
		setPoints([])
	}

	function commitDrawing(captured: Coordinate[]) {
		if (captured.length < 2) return

		// Use appropriate pipeline based on pathMode
		// Sharp: Geometric shapes with 15° angle snapping
		// Smooth: Adaptive smoothing (straight detection + curve smoothing)
		const { points, segments } = style.pathMode === 'curve'
			? processSmoothPath(captured)
			: processSharpPath(captured)

		// Check for player snap on start and end points
		let playerId: string | undefined
		let linkedPointId: string | undefined

		if (players && players.length > 0) {
			const pointIds = Object.keys(points)
			const startPointId = pointIds[0]
			const endPointId = pointIds[pointIds.length - 1]

			if (startPointId) {
				const startPoint = points[startPointId]

				// Check start point first (prioritize)
				if (startPoint) {
					const startSnap = findPlayerSnapTarget(startPoint, players, PLAYER_RADIUS_FEET)
					if (startSnap) {
						playerId = startSnap.playerId
						linkedPointId = startPointId
						// Move start point to player center
						points[startPointId] = { ...startPoint, x: startSnap.point.x, y: startSnap.point.y }
					}
				}

				// Check end point if start didn't snap and end is different from start
				if (!playerId && endPointId && endPointId !== startPointId) {
					const endPoint = points[endPointId]
					if (endPoint) {
						const endSnap = findPlayerSnapTarget(endPoint, players, PLAYER_RADIUS_FEET)
						if (endSnap) {
							playerId = endSnap.playerId
							linkedPointId = endPointId
							// Move end point to player center
							points[endPointId] = { ...endPoint, x: endSnap.point.x, y: endSnap.point.y }
						}
					}
				}
			}
		}

		const drawing: Drawing = {
			id: crypto.randomUUID(),
			points,
			segments,
			style,
			annotations: [],
			// Include player link if found
			...(playerId && linkedPointId ? { playerId, linkedPointId } : {}),
		}

		onCommit(drawing)
	}

	function resizeCanvas() {
		const canvas = canvasRef.current
		if (!canvas) return
		// coordSystem provides untransformed dimensions
		const { width, height } = coordSystem.getDimensions()
		canvas.width = width
		canvas.height = height
		resetCanvas()
	}

	// Resize canvas when dimensions or coordSystem change
	useEffect(() => {
		resizeCanvas()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasDimensions, coordSystem])

	return (
		<canvas
			ref={canvasRef}
			className="absolute inset-0 w-full h-full pointer-events-auto"
			style={{ display: isActive ? 'block' : 'none' }}
			onPointerDown={startDrawing}
			onPointerMove={draw}
			onPointerUp={stopDrawing}
			onPointerLeave={stopDrawing}
		/>
	)
}

